import { __awaiter } from "tslib";
import { AmbientLight, CubeTextureLoader, Group, HemisphereLight, OrthographicCamera, PerspectiveCamera, Raycaster, SRGBColorSpace, Scene, Vector2, Vector3, WebGLRenderer, } from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { disposeObject3D } from "./disposeObject3D";
import { fitCameraToBoundingBox } from "./fitCameraToBoundingBox";
import { fitCameraToMainScene } from "./fitCameraToMainScene";
import { gsap } from "gsap";
const SHOULD_ANIMATE = 3000;
const FOV = 10;
const COLOR_BG_BY_MODE = { dark: 0x000000, light: 0xeae9e7 };
let lastClicked = Date.now();
class Visualizer3D {
    constructor({ node, colorMode = "light", fov = FOV, backgroundColors = COLOR_BG_BY_MODE, initialCameraPosition = { x: 0, y: 1, z: 0 }, initialControlsTarget = { x: 0, y: 0, z: 0 }, spinner = null, addPasses, preserveDrawingBuffer = true, maxPixelRatio = Infinity, }) {
        this.viewportClientRect = { top: 0, left: 0 };
        this._animateTillTime = 0;
        this._isRendering = false;
        this._windowSizeListenerIsConnected = true;
        this._isDisposed = false;
        this.composer = null;
        this.isActive = true;
        this.CAMERA_OFFSET_CONSTANT = 1.07;
        this.SHOULD_ANIMATE_CONSTANT = SHOULD_ANIMATE;
        this.backgroundColors = COLOR_BG_BY_MODE;
        this.spinner = null;
        this.isLoading = false;
        this._fps = 10;
        this._frames = 0;
        this._prevTime = 0;
        this.onWindowScroll = () => {
            this.viewportClientRect = this.node.getBoundingClientRect();
        };
        /** three.js keeps the scene data and re-uploads it when the context is restored */
        this.onContextLost = () => {
            this.stopAnimationLoop();
            console.warn("Visualizer3D: WebGL context lost");
        };
        this.onContextRestored = () => {
            this.shouldAnimate = this.SHOULD_ANIMATE_CONSTANT;
        };
        this.onWindowResize = () => {
            if (!this._windowSizeListenerIsConnected || this._isDisposed) {
                return this;
            }
            this.viewportClientRect = this.node.getBoundingClientRect();
            const { width, height } = this.viewportClientRect;
            this.setWindowSize({
                width,
                height,
                disconnectListener: false,
            });
            return this;
        };
        this.setWindowSize = ({ width, height, disconnectListener = false, }) => {
            var _a;
            if (this._isDisposed)
                return this;
            this._windowSizeListenerIsConnected = !disconnectListener;
            const ASPECT_RATIO = width / height;
            this.cameras.perspective.aspect = ASPECT_RATIO;
            this.cameras.perspective.updateProjectionMatrix();
            this.cameras.orthographic.left = -width / 2;
            this.cameras.orthographic.right = width / 2;
            this.cameras.orthographic.top = height / 2;
            this.cameras.orthographic.bottom = -height / 2;
            this.cameras.orthographic.updateProjectionMatrix();
            this.renderer.setSize(width, height);
            this.renderer.domElement.style.width = `${width}px`;
            this.renderer.domElement.style.height = `${height}px`;
            (_a = this.composer) === null || _a === void 0 ? void 0 : _a.setSize(width, height);
            this.shouldAnimate = this.SHOULD_ANIMATE_CONSTANT;
            return this;
        };
        this.getWindowSize = () => {
            return {
                width: this.viewportClientRect.width,
                height: this.viewportClientRect.height,
            };
        };
        this.animate = () => {
            if (this._isDisposed)
                return;
            this._frames++;
            const time = Date.now();
            if (time >= this._prevTime + 1000) {
                this._fps = this._frames;
                this._frames = 0;
                this._prevTime = time;
            }
            try {
                this.renderFrame();
                if (this._animateTillTime > Date.now()) {
                    if (!this._isRendering)
                        this._isRendering = true;
                    this.controls.update();
                    this.renderer.setAnimationLoop(this.animate);
                }
                else {
                    this._isRendering = false;
                    this.renderer.setAnimationLoop(null);
                }
            }
            catch (e) {
                console.error(e);
                throw e;
            }
        };
        this.renderFrame = () => {
            this.renderer.render(this.scene, this.currentCamera);
        };
        /** Loads an environment map, sets it to .envMap and returns it */
        this.loadEnvMap = (path, faces) => __awaiter(this, void 0, void 0, function* () {
            if (this.envMap)
                return this.envMap;
            return new Promise((resolve) => {
                new CubeTextureLoader().setPath(path).load(faces, (envMap) => {
                    this.envMap = envMap;
                    resolve(envMap);
                });
            });
        });
        this.setCameraInPositionAndTarget = (cameraName_1, cameraPosition_1, controlsTarget_1, ...args_1) => __awaiter(this, [cameraName_1, cameraPosition_1, controlsTarget_1, ...args_1], void 0, function* (cameraName, cameraPosition, controlsTarget, smooth = 0, onEnd) {
            if (!this.cameras[cameraName]) {
                console.warn(`Camera "${cameraName}" does not exist.`);
                return Promise.resolve();
            }
            // Switch camera if it's not the current camera
            if (cameraName !== this.currentCamera.name) {
                yield this.switchCamera(this.scene, cameraName, 0);
            }
            let isResolved = false;
            const controls = this.controls;
            const camera = this.currentCamera;
            return new Promise((resolve) => {
                if (smooth) {
                    if (controlsTarget) {
                        gsap.killTweensOf(controls.target);
                        gsap.to(controls.target, {
                            duration: smooth,
                            x: controlsTarget.x,
                            y: controlsTarget.y,
                            z: controlsTarget.z,
                            ease: "power3",
                            onComplete: () => {
                                if (!isResolved) {
                                    onEnd === null || onEnd === void 0 ? void 0 : onEnd();
                                    resolve();
                                }
                                isResolved = true;
                                controls.update();
                            },
                        });
                    }
                    if (cameraPosition) {
                        gsap.killTweensOf(camera.position);
                        gsap.to(camera.position, {
                            duration: smooth,
                            x: cameraPosition.x,
                            y: cameraPosition.y,
                            z: cameraPosition.z,
                            ease: "power3",
                            onComplete: () => {
                                if (!isResolved) {
                                    onEnd === null || onEnd === void 0 ? void 0 : onEnd();
                                    resolve();
                                }
                                isResolved = true;
                            },
                        });
                    }
                }
                else {
                    if (controlsTarget)
                        this.controls.target.set(controlsTarget.x, controlsTarget.y, controlsTarget.z);
                    if (cameraPosition)
                        this.currentCamera.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);
                    controls.update();
                    resolve();
                }
            });
        });
        const pixRatio = Math.min(window.devicePixelRatio || 1, maxPixelRatio);
        this.node = node;
        this.node.dataset["active"] = this.isActive ? "true" : "false";
        if (spinner) {
            spinner.style.display = "none";
            spinner.className = "tedivo-3d-visualizer-spinner";
            this.node.appendChild(spinner);
            this.spinner = spinner;
        }
        this.cameras = {
            perspective: new PerspectiveCamera(fov, 1, 1, 100000),
            orthographic: new OrthographicCamera(-1, 1, 1, -1, 0.01, 1000000),
        };
        this.cameras.perspective.name = "perspective";
        this.cameras.orthographic.name = "orthographic";
        this.currentCamera = this.cameras.perspective;
        this.currentCamera.lookAt(0, 1, 0);
        const initCamPos = new Vector3(initialCameraPosition.x, initialCameraPosition.y, initialCameraPosition.z);
        const initContTrgt = new Vector3(initialControlsTarget.x, initialControlsTarget.y, initialControlsTarget.z);
        this.cameras.perspective.position.copy(initCamPos);
        //this.cameras.orthographic.position.copy(initCamPos);
        this.scene = new Scene();
        const lightsGroup = new Group();
        lightsGroup.raycast = () => null;
        lightsGroup.name = "lights";
        this.scene.add(lightsGroup);
        const light1 = new AmbientLight(0xffffff, 1.2);
        lightsGroup.add(light1);
        const light2 = new HemisphereLight(0xeeeeee, 0x777777, 1.65);
        lightsGroup.add(light2);
        this.lightsGroup = lightsGroup;
        this.renderer = new WebGLRenderer({
            antialias: true,
            alpha: true,
            logarithmicDepthBuffer: false,
            preserveDrawingBuffer,
        });
        this.renderer.domElement.addEventListener("webglcontextlost", this.onContextLost);
        this.renderer.domElement.addEventListener("webglcontextrestored", this.onContextRestored);
        this.renderer.localClippingEnabled = true;
        this.renderer.outputColorSpace = SRGBColorSpace;
        this.renderer.setClearColor(backgroundColors[colorMode], 1);
        this.renderer.setPixelRatio(pixRatio);
        this.controls = new OrbitControls(this.currentCamera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.25;
        this.controls.enableZoom = true;
        this.controls.enablePan = true;
        this.controls.screenSpacePanning = true;
        this.controls.minDistance = 1;
        this.controls.maxDistance = 5000;
        this.controls.target.copy(initContTrgt);
        this.raycaster = new Raycaster();
        this.mouseVectorWebGlCoords = new Vector2();
        this.mouseVectorScreenCoords = new Vector2();
        if (addPasses) {
            this.composer = new EffectComposer(this.renderer);
            // Add render pass to the composer
            const renderPass = new RenderPass(this.scene, this.currentCamera);
            this.composer.addPass(renderPass);
            // Add passes to the composer
            const passes = addPasses(this.scene, this.currentCamera);
            passes.forEach((pass) => { var _a; return (_a = this.composer) === null || _a === void 0 ? void 0 : _a.addPass(pass); });
            // Add output pass to the composer
            const outputPass = new OutputPass();
            this.composer.addPass(outputPass);
            // Set the render frame function to the composer render (replace the default render frame function)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.renderFrame = () => this.composer.render(0);
        }
        node.appendChild(this.renderer.domElement);
        this.onWindowResize();
    }
    attachEvents({ onClick, onDoubleClick, onMouseMove, onControlsChange, onControlsStart, onControlsEnd, }) {
        let intersects = [];
        let frameCounter = 0;
        this.node.addEventListener("click", () => {
            this.shouldAnimate = 3000;
        });
        this.node.addEventListener("touchstart", () => {
            this.shouldAnimate = 3000;
        });
        this.controls.addEventListener("change", () => {
            this.shouldAnimate = this.SHOULD_ANIMATE_CONSTANT;
            onControlsChange === null || onControlsChange === void 0 ? void 0 : onControlsChange();
        });
        window.addEventListener("resize", this.onWindowResize);
        window.addEventListener("scroll", this.onWindowScroll);
        this.node.addEventListener("click", (e) => {
            var _a;
            const diffClickedInMs = Date.now() - lastClicked;
            // Only recompute if the pointer moved since the last cached raycast
            // (from either a previous click or a mousemove). If it hasn't moved,
            // the cached `intersects` is already correct for this position.
            const moved = this.setMouseVectorToLocalFromEvent(e);
            if (moved) {
                this.raycaster.setFromCamera(this.mouseVectorWebGlCoords.clone(), this.currentCamera);
                intersects = this.raycaster.intersectObjects(this.scene.children, true);
            }
            onClick === null || onClick === void 0 ? void 0 : onClick(intersects, e);
            if (diffClickedInMs < 500 && ((_a = intersects[0]) === null || _a === void 0 ? void 0 : _a.object)) {
                onDoubleClick === null || onDoubleClick === void 0 ? void 0 : onDoubleClick(intersects, e);
            }
            lastClicked = Date.now();
        });
        if (onMouseMove) {
            this.node.addEventListener("mousemove", (e) => {
                if (++frameCounter % 3 !== 0) {
                    return;
                }
                this.setMouseVectorToLocalFromEvent(e);
                this.raycaster.setFromCamera(this.mouseVectorWebGlCoords.clone(), this.currentCamera);
                intersects = this.raycaster.intersectObjects(this.scene.children, true);
                onMouseMove(intersects);
            });
        }
        this.controls.addEventListener("start", () => {
            onControlsStart === null || onControlsStart === void 0 ? void 0 : onControlsStart();
        });
        this.controls.addEventListener("end", () => {
            onControlsEnd === null || onControlsEnd === void 0 ? void 0 : onControlsEnd();
        });
        return this;
    }
    get fps() {
        return this._fps;
    }
    get shouldAnimate() {
        return this._animateTillTime;
    }
    set shouldAnimate(value) {
        if (value) {
            this._animateTillTime = Date.now() + value;
            if (!this._isRendering)
                this.animate();
        }
        else {
            this._animateTillTime = 0;
        }
    }
    get loading() {
        return this.isLoading;
    }
    set loading(value) {
        this.isLoading = value;
        if (value) {
            this.node.removeAttribute("loading");
            if (this.spinner)
                this.spinner.style.display = "block";
            this.shouldAnimate = false;
        }
        else {
            this.node.setAttribute("loading", "");
            if (this.spinner)
                this.spinner.style.display = "none";
            this.shouldAnimate = this.SHOULD_ANIMATE_CONSTANT;
        }
    }
    get active() {
        return this.isActive;
    }
    set active(value) {
        this.isActive = value;
        this.shouldAnimate = value ? this.SHOULD_ANIMATE_CONSTANT : false;
        this.node.setAttribute("data-active", value ? "true" : "false");
        this.controls.enabled = value;
    }
    get mouseWheelZoom() {
        return this.controls.enableZoom;
    }
    set mouseWheelZoom(value) {
        this.controls.enableZoom = value;
    }
    set colorMode(v) {
        this.renderer.setClearColor(this.backgroundColors[v], 1);
    }
    get isDisposed() {
        return this._isDisposed;
    }
    stopAnimationLoop() {
        this._animateTillTime = 0;
        this._isRendering = false;
        this.renderer.setAnimationLoop(null);
    }
    /**
     * Frees the WebGL context and every GPU resource in the scene, and removes the window
     * listeners. The instance can't be used afterwards.
     */
    dispose() {
        var _a, _b;
        if (this._isDisposed)
            return;
        this._isDisposed = true;
        this.stopAnimationLoop();
        window.removeEventListener("resize", this.onWindowResize);
        window.removeEventListener("scroll", this.onWindowScroll);
        const canvas = this.renderer.domElement;
        canvas.removeEventListener("webglcontextlost", this.onContextLost);
        canvas.removeEventListener("webglcontextrestored", this.onContextRestored);
        gsap.killTweensOf(this.controls.target);
        Object.values(this.cameras).forEach((camera) => gsap.killTweensOf(camera.position));
        this.controls.dispose();
        (_a = this.composer) === null || _a === void 0 ? void 0 : _a.dispose();
        disposeObject3D(this.scene);
        (_b = this.envMap) === null || _b === void 0 ? void 0 : _b.dispose();
        this.scene.clear();
        this.renderer.dispose();
        this.renderer.forceContextLoss();
        canvas.remove();
    }
    /** Sets the internal coordinates and returns true if they changed */
    setMouseVectorToLocalFromEvent(e) {
        const { left, top, width, height } = this.viewportClientRect;
        const newX = e.clientX - left;
        const newY = e.clientY - top;
        if (newX === this.mouseVectorScreenCoords.x && newY === this.mouseVectorScreenCoords.y)
            return false;
        this.mouseVectorWebGlCoords.x = ((e.clientX - left) / width) * 2 - 1;
        this.mouseVectorWebGlCoords.y = -((e.clientY - top) / height) * 2 + 1;
        this.mouseVectorScreenCoords.x = newX;
        this.mouseVectorScreenCoords.y = newY;
        return true;
    }
    resetView(object_1, initialY_1) {
        return __awaiter(this, arguments, void 0, function* (object, initialY, smooth = 0.5) {
            this.shouldAnimate = this.SHOULD_ANIMATE_CONSTANT;
            yield fitCameraToMainScene({
                object,
                camera: this.currentCamera,
                offset: this.CAMERA_OFFSET_CONSTANT,
                width: this.viewportClientRect.width,
                height: this.viewportClientRect.height,
                initialY,
                smooth,
                setCameraInPositionAndTarget: this.setCameraInPositionAndTarget,
            });
        });
    }
    fitCameraToBoundingBox(boundingBox_1, direction_1, initialY_1) {
        return __awaiter(this, arguments, void 0, function* (boundingBox, direction, initialY, smooth = 0.5, offset = this.CAMERA_OFFSET_CONSTANT, target) {
            yield fitCameraToBoundingBox({
                boundingBox,
                offset,
                initialY: initialY !== null && initialY !== void 0 ? initialY : (boundingBox.max.y + boundingBox.min.y) / 2,
                smooth,
                direction,
                target,
                camera: this.currentCamera,
                width: this.viewportClientRect.width,
                height: this.viewportClientRect.height,
                setCameraInPositionAndTarget: this.setCameraInPositionAndTarget,
            });
        });
    }
    switchCamera(object, which, delayReturn = 0, initialY) {
        return new Promise((resolve) => {
            const main = () => {
                this.shouldAnimate = false;
                this.currentCamera = which
                    ? this.cameras[which]
                    : this.currentCamera === this.cameras.perspective
                        ? this.cameras.orthographic
                        : this.cameras.perspective;
                this.controls.object = this.currentCamera;
                this.controls.update();
                fitCameraToMainScene({
                    object,
                    camera: this.currentCamera,
                    offset: this.CAMERA_OFFSET_CONSTANT,
                    width: this.viewportClientRect.width,
                    height: this.viewportClientRect.height,
                    initialY,
                    smooth: 0,
                    setCameraInPositionAndTarget: this.setCameraInPositionAndTarget,
                });
                this.onWindowResize();
                this.shouldAnimate = this.SHOULD_ANIMATE_CONSTANT;
                resolve();
            };
            if (delayReturn) {
                setTimeout(main, delayReturn);
            }
            else {
                main();
            }
        });
    }
    takeScreenshot(imageFormat = "image/png", quality = 1) {
        this.renderer.render(this.scene, this.currentCamera);
        const prevAutoClear = this.renderer.autoClear;
        this.renderer.autoClear = false;
        const canvas = this.renderer.domElement;
        const image = canvas.toDataURL(imageFormat, quality);
        this.renderer.autoClear = prevAutoClear;
        return image;
    }
}
export { Box3, Plane, Vector3 } from "three";
export { COLOR_BG_BY_MODE, FOV, SHOULD_ANIMATE, Visualizer3D };
